import { appEnv } from '../config/env';
import { InfoType } from '../App';
import { UserType } from '../stores/authenticated';
import { UsersType } from '../stores/users';
import { each } from 'lodash-es';
import { RoomHistory } from '../components/History/TalkingHistory';
import { DepartmentType } from '../stores/departments';
import { TalkingType } from '../stores/talkings';
import { RoleType } from '../stores/roles';
import { api, ApiResponse } from './api';

export interface SigninRepositoryInterface {
  loginId: string;
  password: string;
}

export interface ChangePasswordRepositoryInterface {
  oldPassword: string;
  newPassword: string;
  newPasswordConfirm: string;
}

export interface RecoverPasswordRepositoryInterface {
  email: string;
}

export interface ResetPasswordRepositoryInterface {
  newPassword: string;
  newPasswordConfirm: string;
  resetPasswordToken: string;
}

export interface SignupRepositoryInterface {
  username: string;
  firstName: string;
  lastName: string;
  departmentId: number;
  email: string;
  password: string;
  phone: string;
  roles: string[];
}

export interface UpdateUserRepositoryInterface {
  firstName: string;
  lastName: string;
  departmentId: number;
  email: string;
  phone: string;
  roles: string[];
  avatar?: FileList;
}

export interface CreateDepartmentRepositoryInterface {
  title: string;
  memberLimit: number;
}

export interface UpdateDepartmentRepositoryInterface extends CreateDepartmentRepositoryInterface {}

export interface RoomLockRepositoryInterface {
  isLocked: boolean;
}

export interface GetSystemRepositoryInterface {
  systemName: string;
  meetingsLimit: number;
  usersLimit: number;
  bgImage: string;
}

export interface UpdateSystemRepositoryInterface {
  systemName: string;
  bgImage?: FileList;
}

export interface OutgoingCallsRepositoryInterface {
  id: number;
  hostUser: UserType;
  guestUser: UserType;
  datetime: string;
  responseStatus: string;
}

export default class Repository {
  apiUrl: string;

  constructor() {
    this.apiUrl = appEnv.apiUrl;
  }

  verifyToken(): Promise<ApiResponse<{ valid: boolean; user: UserType }>> {
    return api.get(`/auth/verify`);
  }

  signin(
    post: SigninRepositoryInterface,
  ): Promise<ApiResponse<{ user: UserType; accessTokenExpiresAt: number }>> {
    return api.post(`/auth/signin`, post);
  }

  logoutRemote(): Promise<ApiResponse<{ ok: true }>> {
    return api.post(`/auth/logout`);
  }

  getInfo(): Promise<ApiResponse<InfoType>> {
    return api.get(`/getInfo`);
  }

  getUser(userId: number): Promise<ApiResponse<UserType>> {
    return api.get(`/users/${userId}`);
  }

  getUsers(): Promise<ApiResponse<UsersType>> {
    return api.get(`/users`);
  }

  updateUser(userId: number, post: UpdateUserRepositoryInterface): Promise<ApiResponse<UserType>> {
    const formData = new FormData();
    each(post, (value: any, key) => {
      if (key === 'roles') {
        each(value, (val) => {
          formData.append(`${key}[]`, val);
        });
      } else if (value.length && (key === 'avatar' || key === 'bgImage')) {
        formData.append(key, value[0]);
      } else {
        formData.append(key, value as any);
      }
    });

    return api.patch(`/users/${userId}`, formData);
  }

  updateUser2(userId: number, post: FormData): Promise<ApiResponse<UserType>> {
    return api.patch(`/users/${userId}`, post);
  }

  removeUser(userId: number) {
    return api.delete(`/users/${userId}`);
  }

  getDepartments(): Promise<ApiResponse<DepartmentType[]>> {
    return api.get(`/departments`);
  }

  createDepartment(post: CreateDepartmentRepositoryInterface): Promise<ApiResponse<DepartmentType>> {
    return api.post(`/departments`, post);
  }

  updateDepartment(
    departmentId: number,
    post: UpdateDepartmentRepositoryInterface,
  ): Promise<ApiResponse<DepartmentType>> {
    return api.patch(`/departments/${departmentId}`, post);
  }

  removeDepartment(departmentId: number): Promise<ApiResponse<any>> {
    return api.delete(`/departments/${departmentId}`);
  }

  getRoles(): Promise<ApiResponse<RoleType[]>> {
    return api.get(`/roles`);
  }

  changePassword(
    userId: number,
    post: ChangePasswordRepositoryInterface,
  ): Promise<ApiResponse<UserType>> {
    return api.patch(`/users/${userId}/changePassword`, post);
  }

  changePrivacy(userId: number, privacyStatus: boolean): Promise<ApiResponse<UserType>> {
    return api.patch(`/users/${userId}/changePrivacy`, { privacyStatus });
  }

  getRoomHistories(userId: number): Promise<ApiResponse<RoomHistory[]>> {
    return api.get(`/${userId}/roomHistories`);
  }

  recoverPassword(post: RecoverPasswordRepositoryInterface): Promise<ApiResponse<any>> {
    return api.post(`/auth/recoverPassword`, post);
  }

  resetPassword(post: ResetPasswordRepositoryInterface): Promise<ApiResponse<any>> {
    return api.post(`/auth/resetPassword`, post);
  }

  signup(post: SignupRepositoryInterface): Promise<ApiResponse<any>> {
    return api.post(`/auth/signup`, post);
  }

  roomLock(roomId: any, post: RoomLockRepositoryInterface): Promise<ApiResponse<TalkingType>> {
    return api.patch(`/room/${roomId}/lock`, post);
  }

  getSystem(): Promise<ApiResponse<GetSystemRepositoryInterface>> {
    return api.get(`/system`);
  }

  updateSystem(post: UpdateSystemRepositoryInterface): Promise<ApiResponse<GetSystemRepositoryInterface>> {
    const formData = new FormData();
    each(post, (value: any, key) => {
      if (value.length && key === 'bgImage') {
        formData.append(key, value[0], 'bg-image.jpg');
      } else {
        formData.append(key, value as any);
      }
    });

    return api.patch(`/system`, formData);
  }

  getOutgoingCalls(userId: number): Promise<ApiResponse<OutgoingCallsRepositoryInterface[]>> {
    return api.get(`/${userId}/outgoingCalls`);
  }

  getIncomingCalls(userId: number): Promise<ApiResponse<OutgoingCallsRepositoryInterface[]>> {
    return api.get(`/${userId}/incomingCalls`);
  }
}
